import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, IconButton, Chip, CircularProgress,
  Divider, Tooltip, LinearProgress, Checkbox, Collapse,
  Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LagbasLogo from '../components/LagbasLogo';
import {
  SECTIONS as STATIC_SECTIONS, PHASES, calcProgress,
  ROLE_LABELS, ROLE_COLORS, loadTemplate
} from '../utils/onboardingTemplate';

// ── Behörighetskontroll per item-roll ──────────────────────────────
function canCheck(itemRole, currentUser, userProfile, onboarding) {
  const r = userProfile?.role || '';
  const uid = currentUser?.uid;
  // Superadmin/VD kan alltid checka
  if (['superadmin', 'vd'].includes(r)) return true;
  // Ansvarig chef
  if (itemRole === 'chef' && ['ao_ansvarig', 'gruppledare'].includes(r)) return true;
  if (itemRole === 'chef' && onboarding?.responsibleUid === uid) return true;
  // IT (admin)
  if (itemRole === 'it' && ['superadmin', 'admin'].includes(r)) return true;
  // Mentor
  if (itemRole === 'mentor' && onboarding?.mentorUid === uid) return true;
  // Ekonomi
  if (itemRole === 'ekonomi' && ['ao_ansvarig', 'vd', 'superadmin'].includes(r)) return true;
  // Medarbetaren själv
  if (itemRole === 'medarbetare' && onboarding?.employeeUid === uid) return true;
  // Fallback: chef-rollen kan checka allt
  if (['ao_ansvarig', 'gruppledare'].includes(r)) return true;
  return false;
}

// ── Progress-ring (SVG) ────────────────────────────────────────────
function BigProgressRing({ value }) {
  const size = 120, r = 50, circ = 2 * Math.PI * r;
  const offset = circ - (value / 100) * circ;
  const color = value === 100 ? '#2E7D32' : value >= 60 ? '#FF6B00' : value >= 30 ? '#1565C0' : '#bdbdbd';
  return (
    <Box sx={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e0e0e0" strokeWidth={10} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={10}
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      </svg>
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h5" fontWeight="bold" sx={{ color, lineHeight: 1 }}>{value}%</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>onboardad</Typography>
      </Box>
    </Box>
  );
}

// ── Fas-progress ───────────────────────────────────────────────────
function PhaseBar({ phase, checks, sections }) {
  const phaseItems = sections.filter(s => s.phase === phase.key).flatMap(s => s.items);
  const done = phaseItems.filter(i => checks?.[i.id]?.done).length;
  const total = phaseItems.length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const color = pct === 100 ? '#2E7D32' : pct > 0 ? '#FF6B00' : '#bdbdbd';
  return (
    <Box sx={{ flex: 1, minWidth: 80 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#666' }}>{phase.label}</Typography>
        <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 700, color }}>
          {done}/{total}
        </Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct}
        sx={{ height: 5, borderRadius: 3,
          bgcolor: '#e0e0e0',
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 } }} />
    </Box>
  );
}

export default function OnboardingDetail() {
  const { id } = useParams();
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();

  const [onboarding, setOnboarding] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [sections, setSections] = useState(STATIC_SECTIONS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(''); // item-id som sparas just nu
  const [expandedSections, setExpandedSections] = useState(new Set());

  const load = useCallback(async () => {
    const [obSnap, empSnap, template] = await Promise.all([
      getDoc(doc(db, 'onboarding', id)),
      getDocs(collection(db, 'users')),
      loadTemplate(db),
    ]);
    if (obSnap.exists()) setOnboarding({ id: obSnap.id, ...obSnap.data() });
    setEmployees(empSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setSections(template);
    setExpandedSections(new Set(template.map(s => s.id)));
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function toggleCheck(itemId, currentDone) {
    if (!onboarding) return;
    setSaving(itemId);
    const newChecks = {
      ...(onboarding.checks || {}),
      [itemId]: currentDone
        ? { done: false, doneBy: null, doneByName: null, doneAt: null }
        : {
            done: true,
            doneBy: currentUser.uid,
            doneByName: userProfile?.displayName || currentUser.email,
            doneAt: new Date(),
          },
    };
    await updateDoc(doc(db, 'onboarding', id), { checks: newChecks, updatedAt: new Date() });
    setOnboarding(prev => ({ ...prev, checks: newChecks }));
    setSaving('');
  }

  function toggleSection(sectionId) {
    setExpandedSections(prev => {
      const next = new Set(prev);
      next.has(sectionId) ? next.delete(sectionId) : next.add(sectionId);
      return next;
    });
  }

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <CircularProgress />
    </Box>
  );

  if (!onboarding) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <Typography color="text.secondary">Onboardingprocessen hittades inte.</Typography>
    </Box>
  );

  const checks = onboarding.checks || {};
  const totalItems = sections.reduce((sum, s) => sum + s.items.length, 0);
  const progress = calcProgress(checks, sections);
  const doneCount = Object.values(checks).filter(c => c?.done).length;
  const mentor = employees.find(e => e.id === onboarding.mentorUid);
  const responsible = employees.find(e => e.id === onboarding.responsibleUid);
  const isComplete = progress === 100;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5' }}>
      {/* Top bar */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate('/onboarding')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Onboarding – {onboarding.employeeName}
            </Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              {doneCount} av {totalItems} aktiviteter klara
            </Typography>
          </Box>
        </Box>
        {isComplete && (
          <Chip icon={<CheckCircleIcon />} label="Fullt onboardad!" size="small"
            sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 700 }} />
        )}
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>

          {/* ── Vänstersida: info + progress ─────────────────────── */}
          <Box sx={{ flex: '0 0 260px', display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* Progress-ring */}
            <Paper sx={{ p: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
              <BigProgressRing value={progress} />
              {isComplete ? (
                <Alert severity="success" sx={{ py: 0.5, width: '100%' }}>
                  <Typography variant="caption" fontWeight={700}>Onboarding klar!</Typography>
                </Alert>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                  {totalItems - doneCount} aktiviteter kvar
                </Typography>
              )}
            </Paper>

            {/* Fasöversikt */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" fontWeight="bold" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.7, fontSize: '0.65rem', display: 'block', mb: 1.5 }}>
                Framsteg per fas
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {PHASES.map(phase => (
                  <PhaseBar key={phase.key} phase={phase} checks={checks} sections={sections} />
                ))}
              </Box>
            </Paper>

            {/* Info */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" fontWeight="bold" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.7, fontSize: '0.65rem', display: 'block', mb: 1.5 }}>
                Process
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {onboarding.startDate && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography variant="caption" color="text.secondary">Dag 1: {onboarding.startDate}</Typography>
                  </Box>
                )}
                {responsible && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography variant="caption" color="text.secondary">
                      Chef: {responsible.displayName || responsible.email}
                    </Typography>
                  </Box>
                )}
                {mentor && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography variant="caption" color="text.secondary">
                      Mentor: {mentor.displayName || mentor.email}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Rollförklaring */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" fontWeight="bold" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.7, fontSize: '0.65rem', display: 'block', mb: 1 }}>
                Rollförklaring
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {Object.entries(ROLE_LABELS).map(([key, label]) => (
                  <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                      bgcolor: ROLE_COLORS[key]?.color || '#aaa' }} />
                    <Typography variant="caption" sx={{ fontSize: '0.68rem' }}>
                      <strong>{label}</strong>
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>

          {/* ── Höger: sektioner med checklistor ─────────────────── */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

            {PHASES.map(phase => {
              const phaseSections = sections.filter(s => s.phase === phase.key);
              if (phaseSections.length === 0) return null;
              const phaseItems = phaseSections.flatMap(s => s.items);
              const phaseDone = phaseItems.filter(i => checks[i.id]?.done).length;
              const phaseTotal = phaseItems.length;
              const phasePct = phaseTotal > 0 ? Math.round((phaseDone / phaseTotal) * 100) : 0;
              return (
                <Box key={phase.key}>
                  {/* Fas-rubrik */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, px: 0.5 }}>
                    <Typography variant="overline" fontWeight="bold" sx={{ color: '#424242', fontSize: '0.7rem' }}>
                      {phase.label}
                    </Typography>
                    <Chip label={`${phaseDone}/${phaseTotal}`} size="small"
                      sx={{ height: 18, fontSize: '0.6rem',
                        bgcolor: phasePct === 100 ? '#E8F5E9' : '#f5f5f5',
                        color: phasePct === 100 ? '#2E7D32' : '#757575' }} />
                    {phasePct === 100 && <CheckCircleIcon sx={{ fontSize: 16, color: '#2E7D32' }} />}
                  </Box>

                  {/* Sektioner */}
                  {phaseSections.map(section => {
                    const sectionDone = section.items.filter(i => checks[i.id]?.done).length;
                    const sectionPct = Math.round((sectionDone / section.items.length) * 100);
                    const expanded = expandedSections.has(section.id);

                    return (
                      <Paper key={section.id} sx={{ mb: 1.5, overflow: 'hidden' }}>
                        {/* Sektionsrubrik */}
                        <Box
                          onClick={() => toggleSection(section.id)}
                          sx={{
                            display: 'flex', alignItems: 'center', px: 2, py: 1.25,
                            cursor: 'pointer', gap: 1.5,
                            borderLeft: `4px solid ${section.color}`,
                            bgcolor: sectionPct === 100 ? '#f9fff9' : 'white',
                            '&:hover': { bgcolor: `${section.bg}80` },
                          }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight={700} sx={{ color: section.color }}>
                                {section.title}
                              </Typography>
                              {sectionPct === 100 && (
                                <CheckCircleIcon sx={{ fontSize: 14, color: '#2E7D32' }} />
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <LinearProgress variant="determinate" value={sectionPct}
                                sx={{ flex: 1, height: 4, borderRadius: 2,
                                  bgcolor: '#e0e0e0',
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: sectionPct === 100 ? '#2E7D32' : section.color,
                                    borderRadius: 2,
                                  } }} />
                              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#999', flexShrink: 0 }}>
                                {sectionDone}/{section.items.length}
                              </Typography>
                            </Box>
                          </Box>
                          <IconButton size="small" sx={{ p: 0.25 }}>
                            {expanded
                              ? <ExpandLessIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                              : <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.disabled' }} />}
                          </IconButton>
                        </Box>

                        {/* Items */}
                        <Collapse in={expanded}>
                          <Box sx={{ px: 0 }}>
                            {section.items.map((item, idx) => {
                              const check = checks[item.id];
                              const done = !!check?.done;
                              const allowed = canCheck(item.role, currentUser, userProfile, onboarding);
                              const isSaving = saving === item.id;
                              const roleStyle = ROLE_COLORS[item.role] || ROLE_COLORS.any;

                              return (
                                <Box key={item.id}
                                  sx={{
                                    display: 'flex', alignItems: 'flex-start', gap: 1,
                                    px: 2, py: 1,
                                    borderTop: idx === 0 ? '1px solid' : '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: done ? '#f9fff9' : 'white',
                                    opacity: done ? 0.85 : 1,
                                    transition: 'background .1s',
                                  }}>
                                  {/* Checkbox */}
                                  {isSaving ? (
                                    <CircularProgress size={18} sx={{ mt: 0.5, flexShrink: 0 }} />
                                  ) : (
                                    <Tooltip title={
                                      !allowed
                                        ? `Endast ${ROLE_LABELS[item.role]} kan bocka av detta`
                                        : done ? 'Klicka för att ångra' : 'Markera som klar'
                                    }>
                                      <span>
                                        <Checkbox
                                          checked={done}
                                          disabled={!allowed}
                                          onChange={() => toggleCheck(item.id, done)}
                                          size="small"
                                          icon={<RadioButtonUncheckedIcon sx={{ fontSize: 20, color: '#bdbdbd' }} />}
                                          checkedIcon={<CheckCircleIcon sx={{ fontSize: 20, color: '#2E7D32' }} />}
                                          sx={{ p: 0.25, mt: 0.2, flexShrink: 0 }}
                                        />
                                      </span>
                                    </Tooltip>
                                  )}

                                  {/* Text */}
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2"
                                      sx={{ textDecoration: done ? 'line-through' : 'none',
                                        color: done ? 'text.disabled' : 'text.primary',
                                        lineHeight: 1.4 }}>
                                      {item.text}
                                      {/* LagbasN-länk */}
                                      {item.lagbasn && (
                                        <Tooltip title={`Öppna i LagbasN: ${item.lagbasn}`}>
                                          <IconButton size="small" component={Link} to={item.lagbasn}
                                            sx={{ ml: 0.5, p: 0.1, color: '#FF6B00', verticalAlign: 'middle' }}>
                                            <OpenInNewIcon sx={{ fontSize: 13 }} />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Typography>
                                    {/* Vem checkade + när */}
                                    {done && check?.doneByName && (
                                      <Typography variant="caption" color="text.disabled"
                                        sx={{ fontSize: '0.65rem', display: 'block', mt: 0.25 }}>
                                        ✓ {check.doneByName}
                                        {check.doneAt?.toDate && ` · ${check.doneAt.toDate().toLocaleDateString('sv')}`}
                                      </Typography>
                                    )}
                                  </Box>

                                  {/* Roll-bricka */}
                                  <Chip
                                    label={ROLE_LABELS[item.role] || item.role}
                                    size="small"
                                    sx={{
                                      height: 18, fontSize: '0.6rem', flexShrink: 0, mt: 0.25,
                                      bgcolor: roleStyle.bg, color: roleStyle.color,
                                    }}
                                  />
                                </Box>
                              );
                            })}
                          </Box>
                        </Collapse>
                      </Paper>
                    );
                  })}
                </Box>
              );
            })}

            {/* Klar-banner */}
            {isComplete && (
              <Alert severity="success" icon={<CheckCircleIcon />}
                sx={{ borderRadius: 2 }}>
                <Typography variant="body2" fontWeight={700}>
                  {onboarding.employeeName} är fullt onboardad!
                </Typography>
                <Typography variant="caption">
                  Alla {totalItems} aktiviteter är avbockade. Välkommen till teamet!
                </Typography>
              </Alert>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
